import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injectable,
  OnInit,
  type Signal,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  Route,
  RoutesRecognized,
} from '@angular/router';
import { filter, map } from 'rxjs';
import { PageContainerComponent, type PageContainerKind } from '@lite-workspace/shared/blocks/page-container';
import { HlmPageSkeleton, type PageSkeletonShape } from './page-skeleton.component';
import { HlmSpinner } from '@lite-workspace/shared/helm-ui/spinner';
import { BOOT_SHUTTER_CAPTION, type BootShutterDocument, HlmBootShutter } from './boot-shutter.component';
import { LeaveBarrierService } from './leave-barrier.service';

/** Where the veil lies: the whole screen, or the content area under a mounted app-shell header. */
export type RouteVeilInset = 'screen' | 'below-header';

/** Tracks mounted below-header veils: while one covers, the screen veil stands down and the header stays usable. */
@Injectable({ providedIn: 'root' })
export class RouteVeilCoverage {
  private readonly contentVeils = signal<readonly Signal<boolean>[]>([]);
  readonly headerCovered = computed(() => this.contentVeils().some(covering => covering()));

  enter(covering: Signal<boolean>): () => void {
    this.contentVeils.update(veils => [...veils, covering]);
    return () => this.contentVeils.update(veils => veils.filter(veil => veil !== covering));
  }
}

/**
 * The app-wide route veil: guard resolves + lazy-chunk loads run before any component paints, so without it a
 * slow route entry is a bare white pause. The short fade-in delay keeps instant navigations clean.
 * Mount it once at the application root, after the router outlet. A shell with its own header mounts a second
 * one with `inset="below-header"`: that one veils only the content, after 1 s, with the skeleton of the page the
 * target route names in `data.veil` (`rows` when it names none), and the root veil yields to it — the header
 * never hides behind a slow guard. A target outside that shell (a full-screen editor) goes back to the root veil.
 */
@Component({
  selector: 'hlm-route-veil',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmBootShutter, HlmPageSkeleton, HlmSpinner, PageContainerComponent],
  template: `
    @if (shown()) {
      @if (inset() === 'below-header') {
        <!-- Mounted from NavigationStart so its delay counts from the click; stays invisible until the
             target route is recognised and its page shape known. -->
        <div
          class="route-veil route-veil-content pointer-events-none fixed inset-x-0 top-[var(--app-shell-header-h)] bottom-0 z-50 overflow-hidden bg-bg"
          [class.invisible]="!shape()"
          aria-label="Загрузка раздела"
        >
          @if (shape(); as pageShape) {
            <app-page-container [kind]="pageKind[pageShape]">
              <hlm-page-skeleton [shape]="pageShape" />
            </app-page-container>
          }
        </div>
      } @else if (bootShutter(); as document) {
        <!-- Entering an editor: its own shutter from the click, no fade-in — the editor mounts the same picture
             at full opacity and keeps it until its first spread, so the hand-off is invisible. -->
        <div class="pointer-events-none fixed inset-0 z-50 flex flex-col bg-bg" [attr.aria-label]="bootShutterCaption[document]">
          <hlm-boot-shutter [document]="document" />
        </div>
      } @else {
        <div
          class="route-veil pointer-events-none fixed inset-0 z-50 grid place-items-center bg-bg"
          aria-label="Загрузка раздела"
        >
          <hlm-spinner class="text-acc" [size]="28" />
        </div>
      }
    }
  `,
  styles: `
    /* Вуаль fixed, поэтому хост не должен занимать слот во флекс-колонке корня. */
    :host {
      display: contents;
    }

    /* The animation is declared HERE, not as a Tailwind arbitrary animate utility: Angular renames
       component @keyframes ("_ngcontent-…_route-veil-in"), so a utility in the global sheet referenced a name
       that no longer existed — no delay, no fade, and every tab switch slammed the veil to full black for a
       frame or two, hiding the persistent shell. Same stylesheet = same rename on both sides. */
    .route-veil {
      animation: route-veil-in var(--t-mid) var(--ease-smooth) var(--t-fast) both;
    }

    /* Owner rule for the cabinet: the first second of a section switch shows nothing new. */
    .route-veil-content {
      animation-delay: var(--t-cinematic);
    }

    @keyframes route-veil-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      /* The delay stays — it is not decoration but the whole «a fast navigation never blinks» rule; only the fade goes. */
      .route-veil {
        animation-duration: 0s;
      }
    }
  `,
})
export class RouteVeilComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly leaveBarrier = inject(LeaveBarrierService);
  private readonly coverage = inject(RouteVeilCoverage);
  private readonly destroyRef = inject(DestroyRef);

  readonly inset = input<RouteVeilInset>('screen');

  // The page container each shaped page renders in (albums grid, faces detail, full-width photos).
  protected readonly pageKind: Record<PageSkeletonShape, PageContainerKind> = { rows: 'grid', faces: 'detail', tiles: 'canvas' };

  // The route whose component mounts this veil (the shell); `null` = mounted outside any routed component.
  private readonly shellRoute = inject(ActivatedRoute, { optional: true })?.routeConfig ?? null;

  // Target page shape from the deepest recognised route's `data.veil`; `undefined` until recognition, `null` outside the shell.
  protected readonly shape = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof RoutesRecognized),
      map(event => (event instanceof RoutesRecognized ? leafVeil(event.state.root, this.shellRoute) : undefined)),
    ),
    { initialValue: undefined },
  );

  protected readonly bootShutterCaption = BOOT_SHUTTER_CAPTION;

  // Screen veil only: a route naming `data.bootShutter` shows the editor's shutter once recognised — unless the
  // navigation starts inside that editor already (album switch, query change), which keeps the plain spinner.
  protected readonly bootShutter = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof RoutesRecognized),
      map(event =>
        event instanceof RoutesRecognized && !bootShutterOf(this.router.routerState.snapshot.root)
          ? bootShutterOf(event.state.root)
          : null,
      ),
    ),
    { initialValue: null },
  );

  // A target outside the shell releases the header: the screen veil takes that navigation.
  private readonly covering = computed(() => this.shape() !== null);

  // True from NavigationStart until the navigation settles (end / cancel / error / skipped).
  private readonly navigating = toSignal(
    this.router.events.pipe(
      filter(
        event =>
          event instanceof NavigationStart ||
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError ||
          event instanceof NavigationSkipped,
      ),
      map(event => event instanceof NavigationStart),
    ),
    { initialValue: false },
  );

  // A leave gate awaiting a real save keeps the screen it is leaving mounted and painted, showing its own
  // «Сохраняем…» — veiling it there would hide that progress and read as an exit that already happened.
  protected readonly shown = computed(
    () =>
      this.navigating() && !this.leaveBarrier.holding() && (this.inset() === 'below-header' || !this.coverage.headerCovered()),
  );

  ngOnInit(): void {
    if (this.inset() === 'below-header') this.destroyRef.onDestroy(this.coverage.enter(this.covering));
  }
}

function bootShutterOf(route: ActivatedRouteSnapshot): BootShutterDocument | null {
  let leaf = route;
  while (leaf.firstChild) leaf = leaf.firstChild;
  return leaf.pathFromRoot.map(step => step.routeConfig?.data?.['bootShutter']).find(Boolean) ?? null;
}

// A route under the veil's own shell that names no shape still gets a skeleton: a slow cabinet navigation never
// goes without feedback. A route leaving the shell (a full-screen editor) gets `null` — the screen spinner covers it.
function leafVeil(route: ActivatedRouteSnapshot, shell: Route | null): PageSkeletonShape | null {
  let leaf = route;
  while (leaf.firstChild) leaf = leaf.firstChild;
  return leaf.pathFromRoot.some(step => step.routeConfig === shell) ? (leaf.data['veil'] ?? 'rows') : null;
}
